import React from 'react';
import { motion } from 'framer-motion';
import { howItWorks } from '../data/constants';

const HowItWorks = () => {
  return (
    <section id="how-it-works" className="section-padding">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-100 mb-4">
            <span className="text-sm font-medium text-blue-700">
              Simple Process
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            How It Works
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Simple on the surface, powerful underneath. Get started in four easy steps.
          </p>
        </motion.div>

        {/* Timeline Steps */}
        <div className="relative">
          {/* Connecting Line */}
          <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-500 via-purple-500 to-emerald-500 transform -translate-x-1/2"></div>

          <div className="space-y-12 md:space-y-0">
            {howItWorks.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                viewport={{ once: true }}
                className={`relative flex flex-col md:flex-row items-center ${
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}
              >
                {/* Step Number and Icon */}
                <div className="flex items-center justify-center w-24 h-24 relative mb-8 md:mb-0 md:w-32 md:h-32">
                  {/* Outer Ring */}
                  <div className="absolute inset-0 rounded-full border-4 border-transparent bg-gradient-to-br from-white to-gray-50 p-1">
                    <div className={`w-full h-full rounded-full border-4 ${step.color} flex items-center justify-center bg-white`}>
                      <div className="text-center">
                        <div className="text-sm font-semibold text-gray-500">Step</div>
                        <div className="text-3xl font-bold gradient-text">{step.step}</div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Floating Icon */}
                  <motion.div
                    animate={{ y: [0, -10, 0] }}
                    transition={{ repeat: Infinity, duration: 2, delay: index * 0.5 }}
                    className="absolute -top-2 -right-2 w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 shadow-lg flex items-center justify-center"
                  >
                    <span className="text-xl text-white">{step.icon}</span>
                  </motion.div>
                </div>

                {/* Step Content */}
                <div className={`flex-1 ${index % 2 === 0 ? 'md:ml-12' : 'md:mr-12'} md:w-1/2`}>
                  <div className="glass-effect rounded-2xl p-8 shadow-lg">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">
                      {step.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {step.description}
                    </p>
                    
                    {/* Progress Indicator */}
                    <div className="mt-6">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-500">Progress</span>
                        <span className="text-sm font-bold gradient-text">
                          {parseInt(step.step) * 25}%
                        </span>
                      </div>
                      <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          whileInView={{ width: `${parseInt(step.step) * 25}%` }}
                          transition={{ duration: 1, delay: 0.5 }}
                          className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Timeline Dot */}
                <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 w-6 h-6 rounded-full bg-white border-4 border-blue-500 z-10"></div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;